import {
  ArrowRight,
  CheckCircle2,
  Eye,
  MoreHorizontal,
  PauseCircle,
  Pencil,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { PromotionItem } from "../../../../redux/type";
import { formatTimeDate } from "../../../../utils/Helper";
import { PROMOTION_TYPE_LABELS } from "../campaignForm";
import CampaignStatusBadge from "./CampaignStatusBadge";

interface CampaignCardProps {
  promotion: PromotionItem;
  onView: (promotion: PromotionItem) => void;
  onEdit: (promotion: PromotionItem) => void;
  onPublish?: (promotion: PromotionItem) => void;
  onUnpublish?: (promotion: PromotionItem) => void;
  onDelete?: (promotion: PromotionItem) => void;
  disableActions?: boolean;
  canEdit?: boolean;
  canPublish?: boolean;
  canDelete?: boolean;
}

export default function CampaignCard({
  promotion,
  onView,
  onEdit,
  onPublish,
  onUnpublish,
  onDelete,
  disableActions,
  canEdit = true,
  canPublish = true,
  canDelete = true,
}: CampaignCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isDraft = promotion.status === "draft" || promotion.status === "paused";
  const hasEligibility =
    (promotion.eligible_regions?.length || 0) > 0 ||
    (promotion.eligible_corridors?.length || 0) > 0;
  const hasMenuActions = canPublish || canDelete;

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[24px]">
      <div className="flex items-start justify-between gap-[16px]">
        <div className="min-w-0">
          <CampaignStatusBadge status={promotion.status} />
          <p className="mt-[6px] text-[16px] font-bold text-[#1E1E1E] truncate">
            {promotion.name}
          </p>
          {promotion.description && (
            <p className="text-[13px] text-[#758089] mt-[2px]">
              {promotion.description}
            </p>
          )}
        </div>
        <p className="shrink-0 text-[14px] font-bold text-[#1E1E1E] text-right">
          {PROMOTION_TYPE_LABELS[promotion.type] || promotion.type || "—"}
        </p>
      </div>

      <div className="mt-[16px] border-t border-[#F0F0F0] pt-[16px] flex items-center justify-between gap-[16px]">
        <div className="flex flex-wrap items-center gap-[10px]">
          <span className="text-[13px] text-[#3F3F46]">
            {formatTimeDate(promotion.starts_at)} -{" "}
            {promotion.ends_at
              ? formatTimeDate(promotion.ends_at)
              : "No end date"}
          </span>
          <span className="text-[12px] text-[#A1A1AA]">
            Redeemed {promotion.times_redeemed}x
          </span>
        </div>

        <div className="flex items-center gap-[8px] shrink-0">
          <button
            type="button"
            onClick={() => onView(promotion)}
            className="inline-flex items-center gap-[6px] rounded-[8px] border border-[#E4E4E7] px-[14px] py-[8px] text-[13px] font-medium text-[#3F3F46]"
          >
            <Eye className="size-[14px]" />
            View Details
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => onEdit(promotion)}
              className="inline-flex items-center gap-[6px] rounded-[8px] border border-[#734792] px-[14px] py-[8px] text-[13px] font-medium text-[#734792]"
            >
              <Pencil className="size-[14px]" />
              Edit
            </button>
          )}
          {hasMenuActions && (
            <div className="relative">
              <button
                type="button"
                disabled={disableActions}
                onClick={() => setMenuOpen((open) => !open)}
                className="flex size-[32px] items-center justify-center rounded-full bg-[#F4F4F5] text-[#758089] disabled:opacity-50"
              >
                <MoreHorizontal className="size-[16px]" />
              </button>
              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-[38px] z-20 w-[160px] rounded-[8px] border border-[#E4E4E7] bg-white py-[6px] shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
                    {canPublish &&
                      (isDraft ? (
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            onPublish?.(promotion);
                          }}
                          className="flex w-full items-center gap-[8px] px-[14px] py-[8px] text-[13px] text-[#3F3F46] hover:bg-[#F4F4F5]"
                        >
                          <CheckCircle2 className="size-[14px]" />
                          Publish
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            onUnpublish?.(promotion);
                          }}
                          className="flex w-full items-center gap-[8px] px-[14px] py-[8px] text-[13px] text-[#3F3F46] hover:bg-[#F4F4F5]"
                        >
                          <PauseCircle className="size-[14px]" />
                          Unpublish
                        </button>
                      ))}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          onDelete?.(promotion);
                        }}
                        className="flex w-full items-center gap-[8px] px-[14px] py-[8px] text-[13px] text-[#DC2626] hover:bg-[#FEF2F2]"
                      >
                        <Trash2 className="size-[14px]" />
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {hasEligibility ? (
        <div className="mt-[12px] flex flex-wrap gap-[8px]">
          {(promotion.eligible_regions || []).map((region) => (
            <span
              key={region}
              className="rounded-full border border-[#E4E4E7] px-[10px] py-[3px] text-[12px] text-[#3F3F46]"
            >
              {region}
            </span>
          ))}
          {(promotion.eligible_corridors || []).map((corridor, idx) => (
            <span
              key={`${corridor?.source}-${corridor?.destination}-${idx}`}
              className="inline-flex items-center gap-[4px] rounded-full border border-[#734792] px-[10px] py-[3px] text-[12px] text-[#734792]"
            >
              {corridor?.source || "—"}
              <ArrowRight className="size-[10px]" />
              {corridor?.destination || "—"}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-[12px] text-[12px] text-[#A1A1AA]">
          All regions &amp; corridors eligible
        </p>
      )}
    </div>
  );
}
